'use client';

import { Menu, MenuItem, Avatar, Divider, Radio, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { addBasePath } from 'next/dist/client/add-base-path';
import { FC, MouseEvent, ReactNode, useContext, useState } from 'react';

import { UserInfo } from '../../../hooks/auth/types';
import { useFullUser } from '../../../hooks/auth/use-user';
import { useBoolean } from '../../../hooks/use-boolean';
import { useLocale } from '../../../hooks/use-locale';
import { useTranslations } from '../../../hooks/use-translations';
import { Locale, usePathname } from '../../../i18n/routing';
import { ColorModeContext } from '../../../theme/ColorContext';
import { UsFlag, RuFlag, LightIcon, DarkIcon } from '../../icons';
import MerchantInfo from '../MerchantInfo/MerchantInfo';
import { ModalDialog } from '../Modal';

import styles from './UserInfo.module.scss';

type UserInfoProps = {};
type UserInfoLanguageListProps = {
  language: Locale;
  setLanguage(language: Locale): void;
};

type UserInfoCardProps = {
  userInfo: UserInfo | null;
};

type UserInfoLanguagePopupProps = {
  modalOpen: boolean;
  language: Locale;
  handleCloseModal: () => void;
  setLanguage(language: Locale): void;
};

const languageOptions: Record<
  Locale,
  {
    name: string;
    flag: ReactNode;
  }
> = {
  ru: {
    name: 'Русский язык',
    flag: <RuFlag />,
  },
  en: {
    name: 'English language',
    flag: <UsFlag />,
  },
};

const supportedLanguages: Locale[] = ['ru', 'en'];

export const UserInfoLanguageList: FC<UserInfoLanguageListProps> = ({ language, setLanguage }) => {
  const t = useTranslations('user.contract');

  return (
    <div className={styles.languageContent}>
      <p className={styles.languageSubtitle}>{t('lang.subTitle')}</p>
      <ul className={styles.languageList}>
        {supportedLanguages.map((code) => {
          const options = languageOptions[code];

          return (
            <li key={code}>
              <label>
                <Radio checked={language === code} onChange={() => setLanguage(code)} />
                {options.flag}
                <span>{options.name}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const UserInfoLanguagePopup: FC<UserInfoLanguagePopupProps> = (props) => {
  const { modalOpen, handleCloseModal, language, setLanguage } = props;

  const t = useTranslations('user.contract');

  const pathname = usePathname();

  const handleChangeLanguage = () => {
    window.location.href = addBasePath(`/${language}${pathname === '/' ? '' : pathname}`);
  };

  return (
    <ModalDialog
      open={modalOpen}
      onClose={handleCloseModal}
      title={t('lang.title')}
      styleOptions={{ width: '25%', minWidth: 'min(400px, 90%)', maxWidth: '520px' }}
      okButton={{
        text: t('lang.save'),
        onClick: handleChangeLanguage,
      }}
      cancelButton={{
        text: t('lang.cancel'),
        onClick: handleCloseModal,
      }}
    >
      <UserInfoLanguageList language={language} setLanguage={setLanguage} />
    </ModalDialog>
  );
};

export const UserInfoCard: FC<UserInfoCardProps> = ({ userInfo }) => {
  return (
    <div className={styles.userInfoContract}>
      <Avatar sx={{ backgroundColor: '#12BA55', width: '48px', height: '48px', marginBottom: '1rem' }}>
        {userInfo?.name?.slice(0, 1)}
      </Avatar>
      <p className={styles.name}>{userInfo?.name ?? userInfo?.email ?? 'userName'}</p>
      <p className={styles.email}>{userInfo?.email ?? 'email'}</p>
    </div>
  );
};

export const UserInfoPanel: FC<UserInfoProps> = () => {
  const locale = useLocale();
  const t = useTranslations('user.contract');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { value: languageModalOpen, setTrue: openLanguageModal, setFalse: closeLanguageModal } = useBoolean(false);
  const [language, setLanguage] = useState(locale);
  const { userInfo, merchantInfo } = useFullUser();

  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (): void => {
    setAnchorEl(null);
  };

  return (
    <>
      <div className={styles.userInfo} onClick={handleOpenMenu}>
        <MerchantInfo userInfo={userInfo} merchantInfo={merchantInfo} />
        <IconButton>
          <Avatar sx={{ backgroundColor: '#12BA55' }}>{userInfo?.name?.slice(0, 1)}</Avatar>
        </IconButton>
      </div>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleCloseMenu}
        onClick={handleCloseMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem className={styles.userInfoContractLine}>
          <UserInfoCard userInfo={userInfo} />
        </MenuItem>
        <Divider />
        <MenuItem className={styles.userOptions} onClick={openLanguageModal}>
          <p>
            {languageOptions[locale].flag}
            <span className={styles.upper}>{locale}</span>
          </p>
        </MenuItem>
        <MenuItem className={styles.userOptions} onClick={colorMode.toggleColorMode}>
          <p>
            {theme.palette.mode === 'dark' ? (
              <>
                <LightIcon /> <span>{t('theme.light')}</span>
              </>
            ) : (
              <>
                <DarkIcon /> <span>{t('theme.dark')}</span>
              </>
            )}
          </p>
        </MenuItem>
      </Menu>

      <UserInfoLanguagePopup
        setLanguage={setLanguage}
        language={language}
        handleCloseModal={closeLanguageModal}
        modalOpen={languageModalOpen}
      />
    </>
  );
};
